/**
 * Work lists — docs/11 §Setup → Work List.
 *
 * "Set up worklists and create customised to-do reminders for your team." Each
 * one is a standing question about the documents: what has been approved and
 * not acted on, what is late, what is waiting for somebody. They are derived,
 * never stored — a to-do that has to be ticked off by hand is a to-do that
 * goes stale.
 *
 * The catalogue is metadata; the query behind each is code, for the same
 * reason the reports are (docs/21 §8): a work list reads across documents, and
 * a configurable query language reading across documents is an injection
 * surface with a friendly name.
 */

export interface WorkListDef {
  readonly key: string
  readonly label: string
  /** The document type it is grouped under on the setup screen. */
  readonly groupedUnder: string
  readonly blurb: string
  /** What clears it, said plainly, because a list nobody can clear is noise. */
  readonly clearedBy: string
  /** Whether a row offers an action of its own, and what to call it. */
  readonly action?: { readonly key: string; readonly label: string }
  /**
   * Off until somebody switches it on: the reference opens Work List Setup
   * with most lists `Not Enabled` (SET1-048). Migration 221 switched on, for
   * every company that existed, each list it had been getting by default.
   */
  readonly enabledByDefault: boolean
  /** Only offered when the company has this setting on. */
  readonly requiresSetting?: string
}

const def = <const T extends WorkListDef>(w: T): T => w

export const WORK_LISTS = [
  def({
    key: 'purchase_plan_to_be_set',
    label: 'Purchase plan to be set for MR',
    groupedUnder: 'material_request',
    blurb: 'Shows approved Material Requests waiting for Purchase Plan to be set. Procurement can begin only after purchase plan is set.',
    clearedBy: 'Setting the purchase plan on the request.',
    action: { key: 'setPurchasePlan', label: 'Plan set' },
    enabledByDefault: false,
    requiresSetting: 'purchase_plan_in_material_request',
  }),
  def({
    key: 'po_to_be_created',
    label: 'PO to be created for MR',
    groupedUnder: 'material_request',
    blurb: 'Shows Material Requests for which Purchase Order is not created yet. Shows only MRs where Procurement Plan is set.',
    clearedBy: 'Raising a purchase order for the request.',
    enabledByDefault: false,
  }),
  def({
    key: 'po_to_be_sent',
    label: 'PO to be emailed to Supplier',
    groupedUnder: 'purchase_order',
    blurb: 'Purchase Order is approved, but email is not sent to Supplier. Use "Mark as Sent" action if you have sent email already.',
    clearedBy: 'Marking the order as sent, once it has gone out.',
    action: { key: 'markSent', label: 'Mark as sent' },
    enabledByDefault: false,
  }),
  def({
    key: 'overdue_deliveries',
    label: 'Overdue Deliveries against PO',
    groupedUnder: 'purchase_order',
    blurb: 'Shows Purchase Orders where GRN is not created beyond Delivery Date. Can be removed from work list by creating GRN or by pre closing PO.',
    clearedBy: 'Recording a receipt note, or cancelling what is not coming.',
    enabledByDefault: false,
  }),
  def({
    key: 'invoice_for_grn',
    label: 'Supplier invoice to be created for GRN',
    groupedUnder: 'goods_receipt',
    blurb: 'Shows approved GRN where Supplier Invoice is not created. Can be removed from work list by creating Purchase Invoice.',
    clearedBy: 'Recording the purchase invoice.',
    enabledByDefault: false,
  }),
  def({
    key: 'sco_to_be_sent',
    label: 'Subcontract Order to be emailed',
    groupedUnder: 'subcontract_order',
    blurb: 'Subcontract Order is approved, but email is not sent to Supplier. Use "Mark as Sent" action if you have sent email already.',
    clearedBy: 'Marking the order as sent.',
    action: { key: 'markSent', label: 'Mark as sent' },
    enabledByDefault: false,
  }),
  def({
    key: 'invoice_for_billing_request',
    label: 'Billing Requests not invoiced',
    groupedUnder: 'billing_request',
    blurb: 'Tax Invoice is not created against Billing Request. Can be removed from work list by creating Tax Invoice or by cancelling the Billing Request.',
    clearedBy: 'Raising the sales invoice.',
    enabledByDefault: false,
  }),
  def({
    key: 'tool_requests_not_issued',
    label: 'Tool Requests not issued',
    groupedUnder: 'tool_request',
    blurb: 'Shows tools that are pending issue',
    clearedBy: 'Issuing the tools against the request, or pre-closing what is no longer needed.',
    enabledByDefault: false,
  }),
  def({
    key: 'tools_still_out',
    label: 'Tools to be returned',
    groupedUnder: 'tool_issue',
    blurb: 'Shows tools that are pending return',
    clearedBy: 'Recording the return, or extending the date on the issue.',
    enabledByDefault: false,
  }),
] as const satisfies readonly WorkListDef[]

export type WorkListKey = (typeof WORK_LISTS)[number]['key']

const byKey = new Map<string, WorkListDef>(WORK_LISTS.map((w) => [w.key as string, w as WorkListDef]))

export const getWorkListDef = (key: string): WorkListDef | undefined => byKey.get(key)

/** One row of a work list: a document, and why it is on the list. */
export interface WorkListRow {
  readonly documentId: string
  readonly docType: string
  readonly route: string
  readonly documentNo: string | null
  readonly documentDate: string
  readonly partyName: string | null
  readonly projectName: string | null
  readonly total: string
  /** Days since it landed here; the reason it is worth looking at. */
  readonly waitingDays: number
  readonly detail: string | null
}

export interface WorkListSummary {
  readonly key: string
  readonly label: string
  readonly blurb: string
  readonly clearedBy: string
  readonly groupedUnder: string
  readonly groupLabel: string
  readonly enabled: boolean
  readonly count: number
  readonly action: { readonly key: string; readonly label: string } | null
  /** WF-004 — when it was last switched on, and who it is for. */
  readonly enabledSince: string | null
  /** Role codes (sign-in or business) and sign-in ids; both empty is everyone. */
  readonly roles: readonly string[]
  readonly userIds: readonly string[]
  readonly assignments: number
  /** Whether the person asking is one of the people it is for. */
  readonly forMe: boolean
}
